import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Store, Users, FileText, TrendingUp, Activity, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate('/');
      return;
    }
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch admin dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!stats) {
    return <div className="text-center text-slate-500">Failed to load dashboard</div>;
  }

  const statCards = [
    {
      title: 'Total Stores',
      value: stats.total_stores,
      icon: Store,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      subtitle: `${stats.active_stores} active`
    },
    {
      title: 'Total Users',
      value: stats.total_users,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      subtitle: 'All roles'
    },
    {
      title: 'Total Customers',
      value: stats.total_customers,
      icon: Building2,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      subtitle: 'Across all stores'
    },
    {
      title: 'Total Tickets',
      value: stats.total_tickets,
      icon: FileText,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      subtitle: `${stats.active_tickets} active`
    },
    {
      title: 'Total Revenue',
      value: `₹${stats.total_revenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
      icon: TrendingUp,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      subtitle: 'Delivered orders'
    },
    {
      title: 'Pending Amount',
      value: `₹${stats.pending_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
      icon: Activity,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      subtitle: 'In progress'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="card-hover" data-testid={`admin-stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {stat.value}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{stat.subtitle}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Stores Summary */}
      <Card data-testid="stores-summary-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Store className="w-5 h-5 text-indigo-600" />
            Stores Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Store Name</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600">Tickets</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {stats.stores_summary.map((store) => (
                  <tr key={store.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <span className="font-medium text-slate-900">{store.name}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={store.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {store.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-center text-slate-600">{store.tickets}</td>
                    <td className="py-3 px-4 text-right font-semibold text-indigo-600">
                      ₹{store.revenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card 
          className="card-hover cursor-pointer" 
          onClick={() => navigate('/admin/stores')}
          data-testid="quick-action-stores"
        >
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="p-4 rounded-xl bg-purple-50">
              <Store className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Manage Stores</h3>
              <p className="text-sm text-slate-500">Create, edit, or disable stores</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="card-hover cursor-pointer" 
          onClick={() => navigate('/admin/users')}
          data-testid="quick-action-users"
        >
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="p-4 rounded-xl bg-blue-50">
              <Users className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Manage Users</h3>
              <p className="text-sm text-slate-500">Add users and assign to stores</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
